import { supabase } from '@/integrations/supabase/client';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import type { AIModel, Message } from '@/types';
import { sendMessageToModel } from '@/services/ModelService';
import { useAppStore } from '@/store';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { slugifyVaultSegment } from '@/lib/librarian/api';

export interface LibrarianSynthesizedTopic {
  name: string;
  summary: string;
  evidence: string[];
  relatedEntities: string[];
}

export interface LibrarianSynthesizedEntity {
  name: string;
  type: string | null;
  summary: string;
  evidence: string[];
  relatedTopics: string[];
}

export interface LibrarianDocumentSynthesis {
  usedAI: boolean;
  summary: string;
  overviewSummary: string;
  keyInsights: string[];
  sourceHighlights: string[];
  openQuestions: string[];
  topics: LibrarianSynthesizedTopic[];
  entities: LibrarianSynthesizedEntity[];
  synthesisMarkdown: string;
  issues: string[];
}

const PROMPT_CHAR_LIMIT = 18000;
const FALLBACK_TOPIC_LIMIT = 5;
const FALLBACK_ENTITY_LIMIT = 5;
const stopWords = new Set([
  'the', 'and', 'for', 'with', 'from', 'that', 'this', 'into', 'about', 'your', 'their', 'have', 'will', 'what', 'when', 'where', 'which', 'while', 'using', 'used', 'over', 'under', 'than', 'then', 'them', 'they', 'been', 'being', 'also', 'after', 'before', 'between', 'through', 'document', 'source', 'knowledge', 'base', 'page', 'pages', 'file', 'files'
]);

function cleanText(text: string, maxLength = PROMPT_CHAR_LIMIT) {
  return text
    .replace(/\u0000/g, ' ')
    .replace(/\r/g, '')
    .replace(/\t/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, maxLength);
}

function summarizeFallback(content: string, maxLength = 420) {
  const cleaned = content.replace(/\s+/g, ' ').trim();
  return cleaned.slice(0, maxLength) + (cleaned.length > maxLength ? '…' : '');
}

function extractHeadings(content: string) {
  return Array.from(content.matchAll(/^#{1,3}\s+(.+)$/gm)).map((match) => match[1].trim());
}

function fallbackTopics(document: KnowledgeBaseDocument) {
  const seeds = `${document.name} ${extractHeadings(document.content).join(' ')}`;
  const tokens = seeds
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 3 && !stopWords.has(token));

  return Array.from(new Set(tokens))
    .slice(0, FALLBACK_TOPIC_LIMIT)
    .map((topic) => ({
      name: topic
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
      summary: `This topic appears in ${document.name}.`,
      evidence: [summarizeFallback(document.content, 180)],
      relatedEntities: [],
    }));
}

function fallbackEntities(document: KnowledgeBaseDocument) {
  const matches = Array.from(document.content.matchAll(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})\b/g)).map((match) => match[1]);
  return Array.from(new Set(matches))
    .slice(0, FALLBACK_ENTITY_LIMIT)
    .map((entity) => ({
      name: entity,
      type: null,
      summary: `${entity} is mentioned in ${document.name}.`,
      evidence: [summarizeFallback(document.content, 180)],
      relatedTopics: [],
    }));
}

function normalizeStringArray(value: unknown, limit: number, maxItemLength = 280) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((item) => item.slice(0, maxItemLength))
    .slice(0, limit);
}

function normalizeTopics(value: unknown) {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();

  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
    .map((item) => {
      const name = typeof item.name === 'string' ? item.name.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      const summary = typeof item.summary === 'string' ? item.summary.trim().slice(0, 600) : '';
      const evidence = normalizeStringArray(item.evidence, 4, 220);
      const relatedEntities = normalizeStringArray(item.relatedEntities, 5, 80);
      return { name, summary, evidence, relatedEntities };
    })
    .filter((item) => item.name && item.summary)
    .filter((item) => {
      const key = item.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 6);
}

function normalizeEntities(value: unknown) {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();

  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
    .map((item) => {
      const name = typeof item.name === 'string' ? item.name.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      const type = typeof item.type === 'string' && item.type.trim() ? item.type.trim().slice(0, 40) : null;
      const summary = typeof item.summary === 'string' ? item.summary.trim().slice(0, 600) : '';
      const evidence = normalizeStringArray(item.evidence, 4, 220);
      const relatedTopics = normalizeStringArray(item.relatedTopics, 5, 80);
      return { name, type, summary, evidence, relatedTopics };
    })
    .filter((item) => item.name && item.summary)
    .filter((item) => {
      const key = item.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 6);
}

function extractJson(text: string) {
  const trimmed = text.trim();

  try {
    return JSON.parse(trimmed);
  } catch {
    const withoutFences = trimmed.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```$/i, '').trim();
    try {
      return JSON.parse(withoutFences);
    } catch {
      const start = withoutFences.indexOf('{');
      const end = withoutFences.lastIndexOf('}');
      if (start >= 0 && end > start) {
        return JSON.parse(withoutFences.slice(start, end + 1));
      }
      throw new Error('Failed to parse AI synthesis JSON.');
    }
  }
}

function buildFallbackSynthesis(document: KnowledgeBaseDocument, issue?: string): LibrarianDocumentSynthesis {
  const topics = fallbackTopics(document);
  const entities = fallbackEntities(document);
  const summary = summarizeFallback(document.content, 520);
  const sourceSlug = slugifyVaultSegment(document.name);

  return {
    usedAI: false,
    summary,
    overviewSummary: `Latest source processed: ${document.name}. ${summary}`,
    keyInsights: [summary],
    sourceHighlights: normalizeStringArray(extractHeadings(document.content), 4, 140),
    openQuestions: [],
    topics,
    entities,
    synthesisMarkdown: [
      `# Synthesis: ${document.name}`,
      '',
      '## Summary',
      summary,
      '',
      '## Topics',
      ...(topics.length > 0 ? topics.map((topic) => `- [${topic.name}](../topics/${slugifyVaultSegment(topic.name)}.md)`) : ['- No strong topics detected yet.']),
      '',
      '## Entities',
      ...(entities.length > 0 ? entities.map((entity) => `- [${entity.name}](../entities/${slugifyVaultSegment(entity.name)}.md)`) : ['- No strong entities detected yet.']),
      '',
      '## Source',
      `- [Source · ${document.name}](../sources/${sourceSlug}.md)`,
    ].join('\n'),
    issues: issue ? [issue] : ['AI synthesis was unavailable, so Librarian used a lightweight fallback.'],
  };
}

function buildLibrarianMessages(document: KnowledgeBaseDocument, content: string): Message[] {
  const timestamp = new Date().toISOString();

  return [
    {
      id: `librarian-system-${document.id}`,
      chatId: document.id,
      role: 'system',
      sender: 'system',
      timestamp,
      content: 'You are Librarian, an expert research synthesizer. Read source text and extract durable knowledge for a wiki-style vault. Return JSON only. Do not wrap the JSON in explanations.'
    },
    {
      id: `librarian-user-${document.id}`,
      chatId: document.id,
      role: 'user',
      sender: 'user',
      timestamp,
      content: [
        'Create a structured synthesis for this source document.',
        '',
        'Return JSON with exactly these keys:',
        '{',
        '  "summary": string,',
        '  "overviewSummary": string,',
        '  "keyInsights": string[],',
        '  "sourceHighlights": string[],',
        '  "openQuestions": string[],',
        '  "topics": [{ "name": string, "summary": string, "evidence": string[], "relatedEntities": string[] }],',
        '  "entities": [{ "name": string, "type": string | null, "summary": string, "evidence": string[], "relatedTopics": string[] }],',
        '  "synthesisMarkdown": string',
        '}',
        '',
        'Rules:',
        '- Focus on durable knowledge from the document, not generic filler.',
        '- Keep topics and entities to the most important 3-6 each.',
        '- Each evidence array should contain short evidence snippets or claims from the source.',
        '- synthesisMarkdown should be rich markdown for a wiki page with sections and bullet points.',
        '- Mention uncertainty in openQuestions when the source is incomplete.',
        '',
        `Document name: ${document.name}`,
        `File type: ${document.file_type}`,
        '',
        'Document content:',
        content,
      ].join('\n')
    }
  ];
}

async function getDefaultModelApiName() {
  const appUser = useAppStore.getState().user;
  if (appUser?.defaultModelApiName) {
    return appUser.defaultModelApiName;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('default_model')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  const profileData = data as { default_model?: unknown } | null;
  const defaultModel = profileData?.default_model;
  return typeof defaultModel === 'string' && defaultModel.trim()
    ? defaultModel.trim()
    : null;
}

async function getAvailableModels() {
  const store = useSupabaseStore.getState();
  if (store.models.length > 0) {
    return store.models;
  }

  await store.fetchModels();
  return useSupabaseStore.getState().models;
}

async function resolveLibrarianModel() {
  const models = await getAvailableModels();
  if (models.length === 0) {
    return { model: null, issue: 'No AI models are available for Librarian synthesis.' };
  }

  const defaultModelApiName = await getDefaultModelApiName();
  const normalizedDefault = defaultModelApiName?.toLowerCase();
  const activeModels = models.filter((model) => model.is_active !== false);
  const candidates = activeModels.length > 0 ? activeModels : models;

  const preferredModel = normalizedDefault
    ? candidates.find((model) => {
        const apiName = model.apiName?.trim().toLowerCase();
        const id = model.id?.trim().toLowerCase();
        return apiName === normalizedDefault || id === normalizedDefault;
      }) ?? null
    : null;

  if (preferredModel) {
    return { model: preferredModel, issue: null };
  }

  const fallbackModel = candidates.find((model) => model.provider === 'openai' && model.apiName === 'gpt-4o-mini')
    ?? candidates.find((model) => model.provider === 'openai')
    ?? candidates[0]
    ?? null;

  return {
    model: fallbackModel,
    issue: defaultModelApiName
      ? `The selected default model (${defaultModelApiName}) was unavailable, so Librarian used ${fallbackModel?.name ?? 'another available model'}.`
      : null,
  };
}

export async function synthesizeDocumentForLibrarian(document: KnowledgeBaseDocument): Promise<LibrarianDocumentSynthesis> {
  const content = cleanText(document.content);
  if (!content) {
    return buildFallbackSynthesis(document, 'The source document did not contain extractable text for AI synthesis.');
  }

  try {
    const { model, issue } = await resolveLibrarianModel();
    if (!model) {
      return buildFallbackSynthesis(document, 'No AI model was available for Librarian synthesis.');
    }

    const response = await sendMessageToModel(
      buildLibrarianMessages(document, content),
      model as AIModel,
      [model as AIModel],
      {
        temperature: 0.2,
        max_tokens: 2200,
      }
    );

    const messageContent = response.content;
    if (typeof messageContent !== 'string' || !messageContent.trim()) {
      throw new Error('AI synthesis returned no content.');
    }

    const parsed = extractJson(messageContent) as Record<string, unknown>;
    const topics = normalizeTopics(parsed.topics);
    const entities = normalizeEntities(parsed.entities);
    const summary = typeof parsed.summary === 'string' ? parsed.summary.trim().slice(0, 1200) : summarizeFallback(document.content, 520);
    const overviewSummary = typeof parsed.overviewSummary === 'string' ? parsed.overviewSummary.trim().slice(0, 1200) : summary;
    const synthesisMarkdown = typeof parsed.synthesisMarkdown === 'string' && parsed.synthesisMarkdown.trim()
      ? parsed.synthesisMarkdown.trim()
      : buildFallbackSynthesis(document).synthesisMarkdown;

    return {
      usedAI: true,
      summary,
      overviewSummary,
      keyInsights: normalizeStringArray(parsed.keyInsights, 6, 220),
      sourceHighlights: normalizeStringArray(parsed.sourceHighlights, 6, 220),
      openQuestions: normalizeStringArray(parsed.openQuestions, 5, 220),
      topics: topics.length > 0 ? topics : fallbackTopics(document),
      entities: entities.length > 0 ? entities : fallbackEntities(document),
      synthesisMarkdown,
      issues: issue ? [issue] : [],
    };
  } catch (error) {
    console.error('Failed to synthesize source document with AI:', error);
    return buildFallbackSynthesis(
      document,
      error instanceof Error ? error.message : 'AI synthesis failed and Librarian used a lightweight fallback.'
    );
  }
}